import { useMemo, useState } from 'react';

import type { VisibilityState } from '@tanstack/react-table';

type UseColumnVisibilityOptions = {
  defaultVisibleIds?: string[];
  storageKey?: string;
};

type UseColumnVisibilityReturn = {
  visibleColumns: string[];
  setVisibleColumns: (ids: string[]) => void;
  columnVisibility: VisibilityState;
};

function readStoredColumnIds(
  storageKey: string,
  allColumnIds: string[]
): string[] | null {
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    const valid = Array.isArray(parsed)
      ? parsed.filter((id) => allColumnIds.includes(id))
      : [];
    return valid.length > 0 ? valid : null;
  } catch {
    return null;
  }
}

export function useColumnVisibility(
  allColumnIds: string[],
  options?: UseColumnVisibilityOptions
): UseColumnVisibilityReturn {
  const { defaultVisibleIds = allColumnIds, storageKey } = options ?? {};

  const [visibleColumns, setVisibleColumnsState] = useState<string[]>(() => {
    if (storageKey) {
      const stored = readStoredColumnIds(storageKey, allColumnIds);
      if (stored) return stored;
    }
    return defaultVisibleIds;
  });

  const setVisibleColumns = (ids: string[]) => {
    setVisibleColumnsState(ids);
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    }
  };

  const columnVisibility = useMemo<VisibilityState>(
    () =>
      Object.fromEntries(
        allColumnIds.map((id) => [id, visibleColumns.includes(id)])
      ),
    [allColumnIds, visibleColumns]
  );

  return { visibleColumns, setVisibleColumns, columnVisibility };
}
